import React, { ReactElement, useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import type { IProjectInfoResData } from '@/store/models/tableEdit.model'
import { techRoadMap } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { wrapText, autoClick } from '@/utils/util'

import * as go from 'gojs'
import { ReactDiagram, ReactOverview } from 'gojs-react'

import './gojs.css' 

type IProjectInfo = IProjectInfoResData & { type: string; fill: string }
interface INodeDataArray {
    key: number;
    question: string;
    actions: { text: string; figure: string; fill: string; }[];
    fill: string
    origin: IProjectInfo
}

export default function TechGraphPop ( { recordId, setViewTechGraphId, workGroupId }: { 
    recordId: number | null
    setViewTechGraphId: React.Dispatch<React.SetStateAction<number | null>>
    workGroupId: number
}): ReactElement {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [, setLogLoading] = useState(false)
    const [nodeDataArray, setNodeDataArray] = useState<INodeDataArray[]>([])
    const [linkDataArray, setLinkDataArray] = useState<{ from: number; to: number; }[]>([])
    const [record, setRecord] = useState<IProjectInfo>()
    const [winH, setWinh] = useState<number>(0)
    const [diagram, setMyDiagram] = useState<any>(null)
    const colors = useRef(['#aeaeff', '#b4c1c1', 'rgba(255,0,0,0.5)', 'rgba(0,255,0,0.5)', 'rgba(0,0,255,0.5)', '#f4d951'])
    // const types = useRef([t('technicalPlan'), t('techIssues'), t('subTech'),
    //     t('technicalDefineAndResearchTaskBook'),
    // ])
    const approvalType = [
        { id: 1, label: t('technicalPlan') },
        { id: 2, label: t('technicalDefineAndResearchTaskBook') },
        { id: 3, label: t('technicalAchievementDelivery') },
        { id: 4, label: t('buTMTApproveTech') },
    ]

    const viewRecord = async (recordId: number) => {
        setIsModalOpen(true)
        try {
            const resData: IProjectInfoResData & {
                listView: any[]
                subList: any[]
            } = await techRoadMap(recordId)
            if (resData.parent) {
                resData.parent = {
                    ...resData.parent,
                    id: resData.parent.id - 1,
                    type: t('technicalPlan'),
                    fill: colors.current[0]
                }
            }
            resData.children = [
                // {
                //     ...resData,
                //     id: resData.id + 1,
                //     type: t('technicalDefineAndResearchTaskBook'),
                //     fill: colors.current[3]
                // },
                // {
                //     ...resData,
                //     id: resData.id + 2,
                //     type: t('subTech'),
                //     fill: colors.current[2]
                // },
                ...resData.listView.map(item => ({
                    ...item,
                    type: approvalType[(Number(item.reviewType) || 1) - 1].label,
                    fill: colors.current[(Number(item.reviewType) || 1) + 1]
                })),
                ...resData.subList.map(item => ({
                    ...item,
                    type: t('subTech'),
                    fill: colors.current[5]
                })),
            ]
            setRecord({
                ...resData,
                type: t('techIssues'),
                fill: colors.current[1]
            })
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
            setLogLoading(false)
        }
    }

    const closePop = () => {
        setIsModalOpen(false)
        setViewTechGraphId(null)
    }

    useEffect(() => {
        setWinh(document.body.clientHeight)
        window.onresize = () => {
            setWinh(document.body.clientHeight)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    useEffect(() => {
        if (recordId) {
            viewRecord(recordId)
        }
    }, [recordId])

    function initDiagram() {
        const $ = go.GraphObject.make
        const myDiagram =
            $(go.Diagram,
                {
                    allowCopy: false,
                    allowMove: false,
                    layout: $(go.TreeLayout,
                        { angle: 90, arrangement: go.TreeLayout.ArrangementFixedRoots }),
                    'undoManager.isEnabled': true
                }
            )

        // when the document is modified, add a "*" to the title and enable the "Save" button
        myDiagram.addDiagramListener('Modified', e => {
            console.log(e)
            // const button = document.getElementById('SaveButton')
            // if (button) button.disabled = !myDiagram.isModified
            const idx = document.title.indexOf('*')
            if (myDiagram.isModified) {
                if (idx < 0) document.title += '*'
            } else {
                if (idx >= 0) document.title = document.title.slice(0, idx)
            }
        })

        // const bluegrad = $(go.Brush, 'Linear', { 0: '#C4ECFF', 1: '#70D4FF' })
        const greengrad = $(go.Brush, 'Linear', { 0: '#B1E2A5', 1: '#7AE060' })
        const mb5 = new go.Margin(0, 0, 5, 0)
        const mr8 = new go.Margin(0, 8, 0, 0)

        // each action is represented by a shape and some text
        const actionTemplate =
            $(go.Panel, 'Horizontal',
                { margin: mb5 },
                $(go.Shape,
                    { width: 12, height: 12, margin: mr8, alignment: go.Spot.TopLeft, },
                    new go.Binding('figure'),
                    new go.Binding('fill'),
                    new go.Binding('stroke')
                ),
                $(go.TextBlock,
                    { font: '10pt Verdana, sans-serif' },
                    new go.Binding('text')
                )
            )

        // each regular Node has body consisting of a title followed by a collapsible list of actions,
        // controlled by a PanelExpanderButton, with a TreeExpanderButton underneath the body
        myDiagram.nodeTemplate =  // the default node template
            $(go.Node, 'Vertical',
                new go.Binding('isTreeExpanded').makeTwoWay(),  // remember the expansion state for
                new go.Binding('wasTreeExpanded').makeTwoWay(), //   when the model is re-loaded
                { selectionObjectName: 'BODY' },
                // the main "BODY" consists of a RoundedRectangle surrounding nested Panels
                $(go.Panel, 'Auto',
                    { name: 'BODY' },
                    $(go.Shape, 'Rectangle',
                        { stroke: null },
                        new go.Binding('fill', 'fill')
                    ),
                    $(go.Panel, 'Vertical',
                        { margin: 3 },
                        // the title
                        $(go.TextBlock,
                            {
                                stretch: go.GraphObject.Horizontal,
                                font: 'bold 12pt Verdana, sans-serif',
                                cursor: 'pointer',
                                click: (e: any, obj: any) => {
                                    console.log('obj.part.data: ', obj.part.data)
                                    if (obj.part.data.origin.reviewType) {
                                        const techName = obj.part.data.question.split(':').pop()
                                        return autoClick(`/relevantByFeedBackList?progressId=${obj.part.data.key}&name=${techName}&type=${obj.part.data.origin.reviewType}`)
                                    }
                                    autoClick(`/projectInfoView?workGroupId=${workGroupId}&projectId=${obj.part.data.key}&isView=true`)
                                }
                            },
                            new go.Binding('text', 'question')
                        ),
                        // the optional list of actions
                        $(go.Panel, 'Vertical',
                            { stretch: go.GraphObject.Horizontal, visible: false },  // not visible unless there is more than one action
                            new go.Binding('visible', 'actions', acts => (Array.isArray(acts) && acts.length > 0)),
                            // headered by a label and a PanelExpanderButton inside a Table
                            $(go.Panel, 'Table',
                                { stretch: go.GraphObject.Horizontal },
                                $('PanelExpanderButton', 'COLLAPSIBLE',  // name of the object to make visible or invisible
                                    { column: 1, alignment: go.Spot.Right }
                                )
                            ), // end Table panel
                            // with the list data bound in the Vertical Panel
                            $(go.Panel, 'Vertical',
                                {
                                    name: 'COLLAPSIBLE',  // identify to the PanelExpanderButton
                                    padding: 2,
                                    stretch: go.GraphObject.Horizontal,  // take up whole available width
                                    background: 'white',  // to distinguish from the node's body
                                    defaultAlignment: go.Spot.Left,  // thus no need to specify alignment on each element
                                    itemTemplate: actionTemplate,  // the Panel created for each item in Panel.itemArray
                                    visible: false
                                },
                                new go.Binding('itemArray', 'actions')  // bind Panel.itemArray to nodedata.actions
                            )  // end action list Vertical Panel
                        )  // end optional Vertical Panel
                    )  // end outer Vertical Panel
                ),  // end "BODY"  Auto Panel
                $(go.Panel,  // this is underneath the "BODY"
                    { height: 17 },  // always this height, even if the TreeExpanderButton is not visible
                    $('TreeExpanderButton')
                )
            )

        // define a second kind of Node:
        myDiagram.nodeTemplateMap.add('Terminal',
            $(go.Node, 'Spot',
                $(go.Shape, 'Circle',
                    { width: 55, height: 55, fill: greengrad, stroke: null }
                ),
                $(go.TextBlock,
                    { font: '10pt Verdana, sans-serif' },
                    new go.Binding('text')
                )
            )
        )

        myDiagram.linkTemplate =
            $(go.Link, go.Link.Orthogonal,
                { deletable: false, corner: 10 },
                $(go.Shape,
                    { strokeWidth: 2 }
                ),
                $(go.TextBlock, go.Link.OrientUpright,
                    {
                        background: 'white',
                        visible: false,  // unless the binding sets it to true for a non-empty string
                        segmentIndex: -2,
                        segmentOrientation: go.Link.None
                    },
                    new go.Binding('text', 'answer'),
                    // hide empty string;
                    // if the "answer" property is undefined, visible is false due to above default setting
                    new go.Binding('visible', 'answer', a => a ? true : false)
                )
            )

        // create the Model with the above data, and assign to the Diagram
        myDiagram.model = new go.GraphLinksModel(
            {
                copiesArrays: true,
                copiesArrayObjects: true,
                nodeDataArray,
                linkDataArray,
                linkKeyProperty :'test'
                // mergeLinkDataArray:linkDataArray
            })
      
        setMyDiagram(myDiagram)
        return myDiagram
    }

    useEffect(() => {
        if (record) {
            console.log(record)
            const nodeDataArray: React.SetStateAction<INodeDataArray[]> = []
            const linkDataArray: React.SetStateAction<{ from: number; to: number }[]> = []
            appendData(record,nodeDataArray)
            if(record.parent){
                appendParent(record,nodeDataArray,linkDataArray)
            }
            if(record.children && record.children.length > 0){
                appendSubChildren(record,nodeDataArray,linkDataArray)
            }
            console.log('nodeDataArray: ', nodeDataArray)
            setNodeDataArray(nodeDataArray)
            setLinkDataArray(linkDataArray)
        }
    }, [record])
    useEffect(() => {
        if (diagram && nodeDataArray.length > 0) {
            diagram.scale = 1
            diagram.commandHandler.scrollToPart(diagram.findNodeForKey(recordId))
        }
    }, [diagram, nodeDataArray])

    function appendData(record: IProjectInfo, nodeDataArray: INodeDataArray[]){
        let techName = ''
        if (record.techName?.includes('"str":') && record.techName?.includes('"obj":')) {
            techName = JSON.parse(record.techName).str
            techName = techName.replace(/<\/?.+?\/?>/g,'')
        } else {
            techName = record.techName ?? record.proposalName
        }
        techName = techName.replace(/\n/g, '')
        const actions = []
        const circleColor = '#999'
        if(record.techPtn){
            actions.push({ text: t('PTN') + ':' + record.techPtn, figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        if(record.subsidiary){
            actions.push({ text: t('subsidiary') + ':' + wrapText(record.subsidiary, 40, '\n'), figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        if(record.tmtapproval){
            actions.push( { text: t('tmtApproval') + ':' + record.tmtapproval, figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        if(record.maturity){
            actions.push({ text: t('maturity') + ':' + record.maturity, figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        if(record.priority){
            actions.push({ text: t('priority') + ':' + record.priority, figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        if(record.techLevel){
            actions.push({ text: t('techLevel') + ':' +  [t('core'), t('key'), t('general'), t('normal')][record.techLevel - 1], figure: 'Circle', fill: circleColor, stroke: circleColor })
        }
        nodeDataArray.push(
            {
                key: record.id,
                question: wrapText(`${record.type}: ${techName}`, 35, '\n'),
                actions: actions,
                fill: record.fill,
                origin: record
            },
        )
    }

    function appendParent(record: IProjectInfo, nodeDataArray: INodeDataArray[], linkDataArray: { from: any; to: any }[]) {
        const parentRecord = {
            ...record.parent,
            type: record.parent?.type ?? '',
            fill: record.parent?.fill ?? ''
        }
        appendData(parentRecord, nodeDataArray)
        linkDataArray.push({ from:parentRecord.id, to:record.id })
        if (parentRecord.parent) {
            appendParent(parentRecord, nodeDataArray, linkDataArray)
        }
    }

    function appendSubChildren(record: IProjectInfo, nodeDataArray: INodeDataArray[], linkDataArray: { from: any; to: any }[]) {
        record.children.forEach(res =>{
            appendData({
                ...res,
                type: res?.type ?? '',
                fill: res?.fill ?? '',
            }, nodeDataArray)
            linkDataArray.push({ from:record.id,to:res.id }) 
            if(res.children && res.children.length > 0){
                appendSubChildren({
                    ...res,
                    type: res?.type ?? '',
                    fill: res?.fill ?? '',
                }, nodeDataArray, linkDataArray)
            }
        })
    }

    function handleModelChange(changes: any) {
        console.log(changes)
    }
    return (
        <Modal title={t('techGraph')} visible={isModalOpen} 
            onOk={closePop}
            onCancel={closePop}
            width='90%'
            centered
            footer={[
                <Button key="submit" type="primary" onClick={closePop}>{t('close')}</Button>
            ]}
        >
            <div style={{ position: 'relative', height: winH - 200 }}>
                <ReactOverview
                    divClassName='overview-component'
                    observedDiagram={diagram}
                />
                <ReactDiagram
                    initDiagram={initDiagram}
                    divClassName='diagram-component'
                    nodeDataArray={nodeDataArray}
                    linkDataArray={linkDataArray}
                    onModelChange={handleModelChange}
                />
            </div>
        </Modal>
    )
}